export interface PackageFilterData {
  // Basic information
  networkType: string;
  bestSeller: string;
  packageGroup: string;
  offerChannel: string;
  packageCode: string;
  offeringCode: string;
  /** Combined product code / number search box — comma/line/space-separated.
   *  Tokens are routed to the backend by prefix (case-insensitive) in the
   *  list's buildFilterRequest: O… → productCode, P… → productNumber; tokens
   *  starting with neither are ignored. Exact-match, OR-ed; mutually exclusive
   *  with the free-text search. */
  productRefs: string;
  /** Exact-match backend `priceId` filter — comma-separated
   *  productOfferingPrice ids. Gets its own box rather than joining
   *  `productRefs` because POP ids carry no O…/P… prefix for that token router
   *  to dispatch on. Resolves to a direct `$in` on the offering document (the
   *  price id is already carried on the inline price ref), so unlike
   *  productCode / productNumber it needs no POP pre-query and never truncates.
   *  Surfaces the PARENT offering with all of its prices — narrowing to just
   *  the matching price is the price-level endpoint's job, not this list's. */
  priceId: string;
  country: string;
  periodTime: string;

  // Marketing classification
  offerClassificationName: string;
  productLine: string;
  /** Exact-match backend `version` filter (productOffering.version,
   *  e.g. "2.0.0"). "" = any. */
  version: string;
  lifecycleStatus: string;
  /** "" = any · "true" / "false" → backend isSellable filter. */
  isSellable: string;
  /** "" = active packages · "only" → backend `deleted=only` (the "Deleted
   *  packages" view): deleted offerings plus live offerings that carry a
   *  deleted package, every row's `prices[]` holding ONLY its deleted packages. */
  deleted: string;

  // Package information
  recuring: string;
  unitDay: string;
  price: string;
  duration: string;
  productType: string;
  unitDataQuota: string;
  lifestyle: string;
  unitSpeed: string;
  dataQuota: string;
  voiceQuota: string;
  speed: string;
  unitPubSpeed: string;
  pubSpeed: string;
}

/** A `PackageFilterData` with every field blank.
 *
 *  Lives here rather than inline in the drawer because the LIST now also
 *  builds one: the toolbar's scoped search commits `productRefs` /
 *  `offeringCode` / `priceId` straight into `appliedFilters`, and when no
 *  drawer filter has ever been applied it has no object to spread. Two
 *  hand-maintained literals would drift the moment a field is added. */
export function createEmptyPackageFilter(): PackageFilterData {
  return {
    networkType: "",
    bestSeller: "",
    packageGroup: "",
    offerChannel: "",
    packageCode: "",
    offeringCode: "",
    productRefs: "",
    priceId: "",
    country: "",
    periodTime: "",
    offerClassificationName: "",
    productLine: "",
    version: "",
    lifecycleStatus: "",
    isSellable: "",
    deleted: "",
    recuring: "",
    unitDay: "",
    price: "",
    duration: "",
    productType: "",
    unitDataQuota: "",
    lifestyle: "",
    unitSpeed: "",
    dataQuota: "",
    voiceQuota: "",
    speed: "",
    unitPubSpeed: "",
    pubSpeed: "",
  };
}

/** Split a code-list field (`offeringCode` / `priceId` / `productRefs`) into
 *  its individual codes. Commas, semicolons, spaces, tabs and new lines all
 *  separate — a column pasted from a spreadsheet must land the same as a
 *  hand-typed comma list — and exact duplicates are dropped so a repeated
 *  code is not sent twice. Exact, not case-folded: offering ids are
 *  nanoid-style and case-sensitive, so two ids differing only in case are
 *  two ids, and folding them would be the silent drop this exists to end.
 *
 *  ONE tokenizer for every code path: the drawer textarea, the toolbar's
 *  scoped box, the wire params and the committed-code chips all read the
 *  same field, so a second split rule would let them disagree about how many
 *  codes are "in" the search. */
export function splitCodeTokens(raw: string | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of (raw ?? "").split(/[\s,;]+/)) {
    const token = piece.trim();
    if (!token) continue;
    if (seen.has(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
}

export function looksLikeProductCodes(term: string): boolean {
  const tokens = splitCodeTokens(term);
  return tokens.length > 0 && tokens.every((t) => /^[op]\d/i.test(t));
}

/** The canonical stored form of a code-list field: `", "`-joined tokens.
 *  Stored (not just sent) normalized so the drawer textarea, the toolbar box
 *  and the chips show the same string after a commit from any of them. */
export function normalizeCodeList(raw: string | null | undefined): string {
  return splitCodeTokens(raw).join(", ");
}

/**
 * A free-text search that is nothing but price (POP) ids — every token
 * starts with `op_` — as the comma list the exact `priceId` filter takes;
 * `""` for anything else. Such a term is looked up by price id instead of
 * being scanned as text.
 */
export function priceIdSearch(raw: string | null | undefined): string {
  const tokens = splitCodeTokens(raw);
  return tokens.length && tokens.every((t) => /^op_./i.test(t))
    ? tokens.join(",")
    : "";
}
